import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import {
  ApplicationDto,
  ApplicationResolution,
  ApplicationStatus,
} from "model/Application";
import {
  CompanyCalendarApplication,
  CompanyCalendarGigDto,
  CompanyCalendarShift,
  CompanyCalendarTimereport,
  Filter,
} from "model/Calendar";
import { CompanyGigApplicationWorker } from "model/Gig";
import { TimeReportResolution, TimeReportStatus } from "model/TimeReport";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCalendarStore } from "web-apps/company/stores/calendarStore/calendarStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { GigDetailsModal } from "../gig/gigDetail/gigDetailsModal.component";
import { FilledCalendarGig } from "./calendarGig/filledCalendarGig.component";
import { NotFilledCalendarGig } from "./calendarGig/notFilledCalendarGig.component";
import { OfferedCalendarGig } from "./calendarGig/offeredCalendarGig.component";
import { WithdrawnCalendarGig } from "./calendarGig/widthdrawnCalendarGig.component";
import { GigApplicationsModal } from "./gigApplicationsModal.component";
import {
  ApplicationRow,
  GigCell,
  GigText,
  ShiftColumn,
  TextBadge,
} from "./weeklyCalendar.styled";
import { CurrentDate } from "./weeklyCalendar.component";

type CalendarGigProps = {
  gigData: CompanyCalendarGigDto;
  gigDataIndex: number;
  compactView: boolean;
  dashboard?: boolean;
  filter: Filter[];
  currentDates: CurrentDate[];
};

type GigsWorkersList = {
  worker: CompanyGigApplicationWorker | null;
  applications: ApplicationDto[] | null;
  shiftWithTimeReport: ShiftWithTimeReport[];
};

type ShiftWithTimeReport = {
  shift: CompanyCalendarShift;
  timeReport: CompanyCalendarTimereport | undefined;
};

export const CalendarGig: React.FC<CalendarGigProps> = ({
  gigData,
  gigDataIndex,
  compactView,
  dashboard,
  filter,
  currentDates,
}) => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const [matchingUnhandledApplications, setMatchingUnhandledApplications] =
    useState<CompanyCalendarApplication[]>([]);

  const [gigsWorkersList, setGigsWorkersList] = useState<GigsWorkersList[]>([]);
  const [gigShiftsByDay, setGigShiftsByDay] = useState<
    Record<number, CompanyCalendarShift[]>
  >({});
  const history = useHistory();
  const [gigModalOpen, setGigModalOpen] = useState(false);
  const [applicationsModalOpen, setApplicationsModalOpen] = useState(false);
  const [hired, setHired] = useState(0);
  const [companyState, companyActions] = useCompanyStore();
  const [, calendarActions] = useCalendarStore();

  useEffect(() => {
    getMatchingUnhandledApplications();

    const allowedDates = new Set(
      currentDates.map((cd: CurrentDate) => cd.date.format("YYYY-MM-DD"))
    );
    const shiftsByDay: Record<number, CompanyCalendarShift[]> = {};

    gigData.shifts.forEach((shift: CompanyCalendarShift) => {
      const dateKey = moment(shift.startTime).format("YYYY-MM-DD");

      if (!allowedDates.has(dateKey)) {
        return;
      }

      let day = moment(shift.startTime).day();
      if (day === 0) {
        day = 7;
      }

      if (!shiftsByDay[day]) {
        shiftsByDay[day] = [];
      }
      shiftsByDay[day].push(shift);
    });

    setGigShiftsByDay(shiftsByDay);
  }, [gigData, currentDates]);

  useEffect(() => {
    getWorkersForGig();
    getAmountHiredWorkers();
  }, [gigData.applications]);

  const getMatchingUnhandledApplications = () => {
    const filteredApplications = gigData.applications.filter(
      (application) => application.status === ApplicationStatus.PENDING
    );

    setMatchingUnhandledApplications(filteredApplications);
  };

  const getAmountHiredWorkers = () => {
    setHired(
      gigData.applications.filter(
        (application) =>
          application.status === ApplicationStatus.CLOSED &&
          application.resolution === ApplicationResolution.ACCEPTED
      ).length
    );
  };

  const getWorkersForGig = () => {
    let assignedWorkers: GigsWorkersList[] = [];
    let unassignedSlots: GigsWorkersList[] = [];

    gigData.applications.forEach((application) => {
      const existingWorkerIndex = assignedWorkers.findIndex(
        (worker) => worker.worker?.id === application.worker.id
      );
      const shiftsWithTimeReports: ShiftWithTimeReport[] = gigData.shifts.map(
        (shift) => ({
          shift,
          timeReport:
            shift.timeReports.find(
              (timeReport) => timeReport.workerId === application.worker.id
            ) || undefined,
        })
      );

      if (existingWorkerIndex === -1) {
        if (
          application.status === ApplicationStatus.OFFERED ||
          application.resolution === ApplicationResolution.ACCEPTED
        ) {
          assignedWorkers.push({
            worker: application.worker as CompanyGigApplicationWorker,
            applications: [application] as ApplicationDto[],
            shiftWithTimeReport: shiftsWithTimeReports,
          });
        }
      } else {
        if (application.status === ApplicationStatus.OFFERED && application) {
          const worker = assignedWorkers[existingWorkerIndex];

          if (!worker.applications) {
            return;
          }

          worker.applications.push(application as ApplicationDto);
        }
      }
    });

    const availableSlots = gigData.amountOfWorkers - assignedWorkers.length;
    if (availableSlots > 0) {
      unassignedSlots = Array.from({ length: availableSlots }, () => ({
        worker: null,
        applications: [],
        shiftWithTimeReport: [],
      }));
    }
    setGigsWorkersList([...assignedWorkers, ...unassignedSlots]);
  };

  const deleteShift = (shiftId: number, gigId: string) => {
    if (companyState.company?.id) {
      companyActions
        .handleDeleteCalenderShift(companyState.company?.id, shiftId, gigId)
        .then(() => {
          calendarActions.updateWeeklyCalendarContext(
            t,
            companyState,
            "UPDATE_WEEKLY_CALENDAR_V2",
            filter
          );

          calendarActions.updateWeeklyCalendarContext(
            t,
            companyState,
            "UPDATE_OVERVIEW_WEEK_CALENDAR_V2",
            filter
          );
        })
        .catch((err: any) => {
          if (
            err.errorMessage ==
            "Unable to delete GigShift due to linked time reports"
          ) {
            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "alert",
                title: t("Alerts.OhNo"),
                message: t(
                  "CompanyTimeReport.UnableToDeleteGigShiftDueToLinkedTimeReports"
                ),
              },
            });
          } else {
            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "alert",
                title: t("Alerts.OhNo"),
                message: t("CompanyTimeReport.UnknownError"),
              },
            });
          }
        });
    }
  };

  return (
    <>
      <GigCell>
        <GigText
          data-tooltip-id={
            gigData.role.length > (dashboard ? 7 : 18) ? "gig-role" : ""
          }
          data-tooltip-content={gigData.role}
          fontSize={dashboard ? FontSize.Small : FontSize.Standard}
          color={Color.MidnightBlue}
          fontFamily={FontFamily.MontserratRegular}
          onClick={() => {
            setGigModalOpen(true);
          }}
        >
          {gigData.role.length > (dashboard ? 7 : 18)
            ? gigData.role.slice(0, dashboard ? 7 : 19) + "..."
            : gigData.role}
        </GigText>

        {gigData.role.length > 18 && (
          <Tooltip
            place="top"
            id="gig-role"
            style={{
              backgroundColor: Color.White,
              color: Color.MidnightBlue,
              fontFamily: FontFamily.MontserratRegular,
              boxShadow: "0 4px 15px 0 rgba(185, 185, 185, 0.5)",
            }}
          />
        )}
        {!compactView && (
          <>
            <ApplicationRow
              onClick={() => {
                setApplicationsModalOpen(true);
                matchingUnhandledApplications.length > 0 &&
                  history.push("?status=Pending");
              }}
            >
              <GigText
                fontSize={FontSize.Small}
                color={Color.MidnightBlue}
                fontFamily={FontFamily.MontserratSemiBold}
              >
                {`${hired} / ${gigData.amountOfWorkers} ${t(
                  "CalendarCompany.Hired"
                )}`}
              </GigText>
              {matchingUnhandledApplications.length > 0 && (
                <TextBadge>{matchingUnhandledApplications.length}</TextBadge>
              )}
            </ApplicationRow>
          </>
        )}
      </GigCell>

      {currentDates.map((currentDate) => {
        let day = currentDate.date.day();
        if (day === 0) {
          day = 7;
        }

        const shiftsForDay = gigShiftsByDay[day] || [];

        return (
          <ShiftColumn
            key={currentDate.date.format("YYYY-MM-DD")}
            column={day}
            row={gigDataIndex}
          >
            {shiftsForDay.map((shift: CompanyCalendarShift) =>
              gigsWorkersList.map(
                (workerData: GigsWorkersList, index: number) => {
                  const workerMatchingTimeReport =
                    workerData.shiftWithTimeReport.find(
                      (shiftWithTimeReport: ShiftWithTimeReport) =>
                        shiftWithTimeReport.shift.id === shift.id &&
                        shiftWithTimeReport.timeReport
                    );

                  const keyId = shift.id + index;
                  const workerName = workerData.worker?.firstName || "";

                  const application = workerData.applications?.[0];

                  const timeReport = workerMatchingTimeReport?.timeReport;
                  const isWithdrawn =
                    timeReport &&
                    timeReport.status == TimeReportStatus.CLOSED &&
                    timeReport.resolution == TimeReportResolution.WITHDRAWN;

                  if (timeReport && !isWithdrawn) {
                    return (
                      <FilledCalendarGig
                        name={workerName}
                        keyId={keyId}
                        timeReport={timeReport}
                        compactView={compactView}
                        dashboard={dashboard}
                        gigDataId={gigData.id}
                        gigData={gigData}
                      />
                    );
                  }

                  if (
                    application &&
                    [
                      ApplicationStatus.OFFERED,
                      ApplicationStatus.PENDING,
                    ].includes(application.status)
                  ) {
                    return (
                      <OfferedCalendarGig
                        name={workerName}
                        shift={shift}
                        keyId={keyId}
                        compactView={compactView}
                        dashboard={dashboard}
                        gigData={gigData}
                        deleteShift={deleteShift}
                        workerDataId={workerData.worker?.id}
                      />
                    );
                  }

                  if (
                    application &&
                    application.status === ApplicationStatus.CLOSED &&
                    application.resolution === ApplicationResolution.ACCEPTED &&
                    isWithdrawn
                  ) {
                    return (
                      <WithdrawnCalendarGig
                        name={workerName}
                        keyId={keyId}
                        timeReport={timeReport}
                        compactView={compactView}
                        dashboard={dashboard}
                        gigData={gigData}
                        shift={shift.id}
                      />
                    );
                  } else {
                    return (
                      <NotFilledCalendarGig
                        name={workerName}
                        shift={shift}
                        keyId={keyId}
                        compactView={compactView}
                        dashboard={dashboard}
                        gigData={gigData}
                        deleteShift={deleteShift}
                        filter={filter}
                      />
                    );
                  }
                }
              )
            )}
          </ShiftColumn>
        );
      })}

      {gigModalOpen && (
        <GigDetailsModal
          gigId={gigData.id}
          setModalOpen={setGigModalOpen}
          modalOpen
        />
      )}

      <GigApplicationsModal
        gigData={gigData}
        setModalOpen={setApplicationsModalOpen}
        modalOpen={applicationsModalOpen}
      />
    </>
  );
};
